import { ChangeEvent, useContext, useEffect, useState } from "react";
import {
  Controller,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import styled from "styled-components";
import theme from "../styles/theme";
import { PillarTypeDetail } from "../types/Roadmap";
import Button from "./Button";
import FlexBox, { FlexGrow } from "./FlexBox";
import Input, { InputTextArea } from "./Input";
import { StyledText } from "./Text";
import { Icon as MuiIcon } from "@mui/material";
import Icon, { ClickableIcon } from "./Icon";
import Status from "../types/enum/Status";
import { useNavigate } from "react-router-dom";
import IconPickerModal from "./IconPickerModal";
import { ColorResult, SketchPicker } from "react-color";
import { UserContext } from "../context/UserContext";
import ConfirmChanges from "./ConfirmChanges";

export const CustomFlexFixed = styled(FlexBox)`
  width: 100%;
  position: fixed;
  bottom: 0;
  right: 0px;
  background: #ffffff;
  padding: 6px 0px;
  box-shadow: 0 75px 45px 45px black;
  // z-index: 9999;
`;
const CustomFlexPlain = styled(FlexBox)`
  padding: 20px 16px;
  align-items: flex-start;
`;
const CustomGrowPlain = styled(FlexGrow)`
  min-width: 150px;
  flex-grow: 0;
`;
const Label = styled.h5`
  text-transform: uppercase;
  color: ${theme.color.darkBlueGray900};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
`;
type PillarModifyType = {
  pillarData: PillarTypeDetail;
  showBackButton?: boolean;
  goBack: () => void;
  onSaveComplete: (data: any) => void;
};
interface Inputs {
  title: string;
  description: string;
  icon: string;
  color: string;
  id: string;
  // pillar_code: string;
  pillar_prefix: string;
  is_core: boolean;
  ordinal: number;
}
export const SaveButton = styled(Button)`
  background: ${({ theme }) => theme.color.blueButton};
  box-shadow: 0px 2px 5px #808080cc;
  border: none;
  width: 140px;
  min-height: 40px;
`;
export const CancelButton = styled(Button)`
  margin-right: 10px;
  vertical-align: bottom;
  background: ${({ theme }) => theme.color.deleteButton};
  box-shadow: 0px 2px 5px #808080cc;
  border: none;
  width: 120px;
  min-height: 40px;
`;

function PillarModify({
  pillarData,
  goBack,
  showBackButton = false,
  onSaveComplete,
}: PillarModifyType) {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { isDirty, errors },
  } = useForm<Inputs>({ mode: "onBlur" });
  const [iconPickerOpen, setIconPickerOpen] = useState<boolean>(false);
  const { setModified, isOpen, setOpen } = useContext(UserContext);
  const onSubmit: SubmitHandler<Inputs> = (data: any) => {
    setOpen(false);
    setModified(false);
    reset();
    mapData(pillarData);
    onSaveComplete(data);
  };

  useEffect(() => {
    reset();
    mapData(pillarData);
  }, [pillarData]);

  useEffect(() => {
    isDirty && setModified(true);
  }, [isDirty]);

  const mapData = (data: PillarTypeDetail) => {
    setValue("title", data.title);
    setValue("description", data.description);
    setValue("icon", data.graphics.icon);
    setValue("color", data.graphics.iconbg);
    setValue("id", data.id);
    setValue("is_core", data.is_core);
    setValue("ordinal", data.ordinal);
    // setValue("pillar_code", data.code);
    setValue("pillar_prefix", data.prefix);
  };
  const [count, setCount] = useState({ name: 0, description: 0 });

  const discardFunction = () => {
    setOpen(false);
    setModified(false);
    reset();
    mapData(pillarData);
  };

  return (
    <>
      {isOpen && (
        <ConfirmChanges
          discardFunction={discardFunction}
          isOpen={isOpen}
          setOpen={setOpen}
          confirmFunction={handleSubmit(onSubmit)}
          title={"Do you want to save your changes?"}
          description={
            "If you leave page, any changes you have made will be lost."
          }
        />
      )}
      <IconPickerModal
        isOpen={iconPickerOpen}
        setOpen={setIconPickerOpen}
        onSubmit={(icon) => {
          setValue("icon", icon);
          setIconPickerOpen(false);
        }}
        data={{ icon: watch().icon }}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Pillar Name</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <FlexBox
              space={0}
              alignItems="start"
              style={{ width: "100%" }}
              direction="column"
            >
              <FlexGrow style={{ width: "100%" }}>
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <Input
                      isFullWidth
                      maxLength={64}
                      value={field.value}
                      onChange={(e: any) => {
                        field.onChange(e);
                        setCount({ ...count, name: e.target.value.length });
                      }}
                    />
                  )}
                />
                {/* <Input isFullWidth {...register("title", { required: true })} /> */}
              </FlexGrow>
              <FlexGrow style={{ width: "60%", textAlign: "end" }}>
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <StyledText
                      style={{ fontSize: "14px" }}
                      color={theme.color.lightBlueGray500}
                    >
                      {count.name || field.value?.length}/64
                    </StyledText>
                  )}
                />
              </FlexGrow>
            </FlexBox>
            {errors.title && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                This field is required
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Pillar Description</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <FlexBox
              space={0}
              alignItems="start"
              style={{ width: "100%" }}
              direction="column"
            >
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <>
                    <FlexGrow style={{ width: "100%" }}>
                      <InputTextArea
                        isFullWidth
                        style={{ minHeight: "100px" }}
                        value={field.value}
                        maxLength={256}
                        onChange={(e: any) => {
                          field.onChange(e);
                          setCount({
                            ...count,
                            description: e.target.value.length,
                          });
                        }}
                      />
                    </FlexGrow>
                    <FlexGrow style={{ width: "60%", textAlign: "end" }}>
                      <StyledText
                        style={{ fontSize: "14px" }}
                        color={theme.color.lightBlueGray500}
                      >
                        {count.description || field.value?.length}/256
                      </StyledText>
                    </FlexGrow>
                  </>
                )}
              />
              {/* <InputTextArea
                  isFullWidth
                  style={{ minHeight: "100px" }}
                  // onChange={e => setCount(e.target.value.length)}
                  {...register("description")}
                /> */}
            </FlexBox>
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Icon</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <>
              {watch().icon && watch().icon !== "" && (
                <MuiIcon
                  sx={{
                    color: "white",
                    padding: "6px",
                    background: watch().color,
                    boxShadow: watch().color,
                    borderRadius: "100px",
                    fontSize: "18px",
                    verticalAlign: "middle",
                    marginRight: "5px",
                  }}
                >
                  {watch().icon}
                </MuiIcon>
              )}
              <Button type="button" onClick={() => setIconPickerOpen(true)}>
                Select Icon
              </Button>
            </>
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain style={{ marginBottom: "50px" }}>
          <CustomGrowPlain>
            <Label>Color</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <Controller
              control={control}
              name="color"
              render={({ field: { onChange, value } }) => (
                <SketchPicker
                  color={value}
                  onChangeComplete={(
                    color: ColorResult,
                    event: ChangeEvent<HTMLInputElement>
                  ) => {
                    onChange(color.hex);
                  }}
                />
              )}
              rules={{ required: "This is required!" }}
            />
            {errors.color && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                This field is required
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexFixed>
          {showBackButton && (
            <Button
              key={0}
              style={{ marginLeft: "30px" }}
              type="button"
              onClick={() => {
                goBack();
              }}
              status={Status.primary}
              isOutline
            >
              <Icon style={{ paddingRight: "8px" }} name={"arrow_back"} /> Back
            </Button>
          )}
          <FlexGrow
            style={{
              textAlign: "end",
              marginRight: "30px",
              paddingTop: 10,
              paddingBottom: 10,
            }}
          >
            <CancelButton
              className="reduce_opct_onhover"
              key={1}
              type="button"
              onClick={() => {
                navigate("/modify/pillar");
              }}
              status={Status.danger}
              isOutline={false}
            >
              Cancel
            </CancelButton>
            <SaveButton
              className="reduce_opct_onhover"
              key={2}
              type="submit"
              status={Status.primary}
              isOutline={false}
            >
              <Icon style={{ paddingRight: "8px" }} name={"save"} /> Save
            </SaveButton>
          </FlexGrow>
        </CustomFlexFixed>
      </form>
    </>
  );
}
export default PillarModify;
